import{useEffect,useId,useState}from'react';
import{
 Alert,Box,Button,Card,CardContent,Chip,CircularProgress,Collapse,IconButton,LinearProgress,
 Paper,Stack,Tooltip,Typography,type ButtonProps
}from'@mui/material';
import{useIsFetching,useIsMutating}from'@tanstack/react-query';
import RasanaLogo from'./RasanaLogo';
import{InfoOutlined}from'@mui/icons-material';
import{rasanaTokens}from'./theme';

export function PageHeader({
 title,subtitle,action,status
}:{title:string;subtitle?:string;action?:React.ReactNode;status?:React.ReactNode}){
 const[detailsOpen,setDetailsOpen]=useState(false);
 const guidanceId=useId();
 return <Stack direction={{xs:'column',sm:'row'}}
  justifyContent="space-between" alignItems={{sm:'flex-start'}} spacing={1.5} mb={2.25}>
  <Box sx={{minWidth:0}}>
   <Stack direction="row" alignItems="center" spacing={0.5}>
    <Box sx={{width:3,height:27,borderRadius:2,bgcolor:rasanaTokens.gold,mr:1.25,flexShrink:0}}/>
    <Typography variant="h4" sx={{fontSize:{xs:23,sm:28},fontWeight:650,letterSpacing:'-.02em'}}>
     {title}
    </Typography>
    {subtitle&&<Tooltip title={detailsOpen?'Hide page guidance':'About this page'}>
     <IconButton size="small" color={detailsOpen?'primary':'default'}
      aria-label={detailsOpen?'Hide page guidance':'About this page'}
      aria-expanded={detailsOpen} aria-controls={guidanceId}
      onClick={()=>setDetailsOpen(x=>!x)}>
      <InfoOutlined fontSize="small"/>
     </IconButton>
    </Tooltip>}
   </Stack>
   {subtitle&&<Collapse in={detailsOpen} id={guidanceId}>
    <Typography variant="body2" color="text.secondary"
     sx={{mt:1,ml:2.5,maxWidth:740,overflowWrap:'anywhere'}}>
     {subtitle}
    </Typography>
   </Collapse>}
   {status&&<Box sx={{mt:1,display:'flex',flexWrap:'wrap',alignItems:'center',gap:1}}
    aria-label="Status information">{status}</Box>}
  </Box>
  {action&&<Box sx={{
   pt:{sm:.5},minWidth:0,width:{xs:'100%',sm:'auto'},
   maxWidth:'100%',
   '& > .MuiButtonBase-root':{width:{xs:'100%',sm:'auto'}},
   '@media (max-width:599.95px)':{
    '& > .MuiStack-root':{
     display:'flex',flexDirection:'column',alignItems:'stretch',
     gap:1,width:'100%',margin:0
    },
    '& > .MuiStack-root > *':{margin:'0 !important',minWidth:0},
    '& .MuiButtonBase-root':{width:'100%',minHeight:44},
    '& .MuiFormControl-root':{width:'100%',minWidth:'0 !important'}
   },
   '@media (min-width:600px)':{
    '& > .MuiStack-root':{flexWrap:'wrap',justifyContent:'flex-end',rowGap:1}
   }
  }}>
   {action}
  </Box>}
 </Stack>;
}


/** One alignment rule for page toolbars, filter actions, and card action areas.
 * Primary actions remain visible; secondary buttons wrap on tablets and stack on phones. */
export function ResponsiveActionBar({
 children,align='start',compact=false
}:{children:React.ReactNode;align?:'start'|'end';compact?:boolean}){
 return <Box sx={{
  width:'100%',minWidth:0,display:'flex',
  flexDirection:{xs:'column',sm:'row'},
  justifyContent:align==='end'?'flex-end':'flex-start',
  alignItems:{xs:'stretch',sm:'center'},
  flexWrap:'wrap',gap:compact?1:1.25,
  '& > *':{minWidth:0,margin:'0 !important'},
  '& > .MuiButtonBase-root':{
   flexShrink:0,minHeight:44,
   width:{xs:'100%',sm:'auto'},whiteSpace:{xs:'normal',sm:'nowrap'}
  },
  '& > .MuiFormControl-root':{
   width:{xs:'100%',sm:'auto'},flex:{xs:'none',sm:'1 1 220px'},
   maxWidth:'100%'
  },
  '& > .MuiStack-root':{
   flexDirection:{xs:'column',sm:'row'},gap:1,
   alignItems:{xs:'stretch',sm:'center'},
   flexWrap:'wrap',margin:'0 !important',
   '& > *':{margin:'0 !important'},
   '& .MuiButtonBase-root':{width:{xs:'100%',sm:'auto'},minHeight:44}
  }
 }}>{children}</Box>;
}

/** Optional, contextual assistance. Keeps procedural instructions available
 * without repeating long banners across an operational workspace. */
export function SectionHelp({
 title='How it works',children
}:{title?:string;children:React.ReactNode}){
 const[open,setOpen]=useState(false);
 const guidanceId=useId();
 return <Box sx={{alignSelf:'flex-start',maxWidth:'100%'}}>
  <Button variant="text" size="small" color="inherit"
   aria-expanded={open} aria-controls={guidanceId}
   aria-label={open?'Hide '+title:title}
   onClick={()=>setOpen(x=>!x)} sx={{color:'text.secondary',px:.75}}>
   <InfoOutlined fontSize="small" sx={{mr:.75}}/>
   {open?'Hide guidance':title}
  </Button>
  <Collapse in={open} id={guidanceId}>
   <Box sx={{mt:1,p:1.5,border:'1px solid',borderColor:'divider',
    borderRadius:1.5,bgcolor:'action.hover',maxWidth:760}}>
    <Typography variant="body2" color="text.secondary" component="div">
     {children}
    </Typography>
   </Box>
  </Collapse>
 </Box>;
}

export function Kpi({label,value,detail}:{
 label:string;
 value:React.ReactNode;
 detail?:React.ReactNode;
}){
 return <Card sx={{
  height:'100%',
  position:'relative',
  overflow:'hidden',
  '&::before':{
   content:'""',
   position:'absolute',
   left:0,
   top:0,
   bottom:0,
   width:3,
   bgcolor:rasanaTokens.primary
  }
 }}>
  <CardContent>
   <Typography
    color="text.secondary"
    variant="body2"
    fontWeight={500}
   >
    {label}
   </Typography>

   <Typography
    sx={{
     mt:.75,
     fontSize:{xs:22,sm:26,md:30},
     overflowWrap:'anywhere',
     lineHeight:1.15,
     fontWeight:600,
     color:rasanaTokens.navy
    }}
   >
    {value}
   </Typography>

   {detail&&
    <Typography
     variant="caption"
     color="text.secondary"
     sx={{display:'block',mt:.75}}
    >
     {detail}
    </Typography>
   }
  </CardContent>
 </Card>;
}

export function Health({value}:{value:string}){
 const color=value==='Green'
  ?'success'
  :value==='Amber'
    ?'warning'
    :value==='Red'
      ?'error'
      :'default';

 return <Chip
  label={value??'Not set'}
  color={color as any}
  size="small"
  variant="filled"
  sx={{
   height:26,
   fontWeight:700,
   cursor:'default',
   pointerEvents:'none',
   bgcolor:value==='Green'?'#E6F3EB':value==='Amber'?'#FFF2D6':
    value==='Red'?'#FCE7E7':'action.hover',
   color:value==='Green'?'#246942':value==='Amber'?'#855C08':
    value==='Red'?'#992D2D':'text.secondary',
   '& .MuiChip-label':{px:1.25}
  }}
 />;
}

/** Preserve actionable server messages; translate only low-level failures.
 * The API remains responsible for business validation and authorisation. */
export function errorMessage(error:unknown):{message:string;raw:string}{
 const raw=error instanceof Error?error.message:String(error??'');
 if(/(?:Failed to fetch|NetworkError|ERR_NETWORK)/i.test(raw))
  return{message:'Rasana could not reach the service. Check your connection and try again.',raw};
 if(/(?:Cannot (GET|POST|PUT|PATCH|DELETE) \/|404 Not Found)/i.test(raw))
  return{message:'This operation is not available in the current Rasana release. Ask your administrator to check the deployment.',raw};
 if(/(?:^|\s)(401|Unauthorized)(?:\s|$|:)/i.test(raw))
  return{message:'Your session may have expired. Sign in again to continue.',raw};
 if(/(?:^|\s)(403|Forbidden)(?:\s|$|:)/i.test(raw))
  return{message:'You do not have permission to perform this action for the current Organisation or Department.',raw};
 return{message:raw,raw};
}
export function ErrorBox({error}:{error:unknown}){
 if(!error)return null;
 const{message,raw}=errorMessage(error);
 return<Alert severity="error" sx={{mb:2}}>
  {message}
  {raw!==message&&<SectionHelp title="Technical details">
   <Box sx={{overflowWrap:'anywhere'}}>{raw}</Box>
  </SectionHelp>}
 </Alert>;
}

export function EmptyState({
 title='Nothing here yet',
 description='There is no data to display.'
}:{title?:string;description?:string}){
 return <Paper
  variant="outlined"
  sx={{
   p:{xs:3,md:4},
   textAlign:'center',
   bgcolor:rasanaTokens.ivory
  }}
 >
  <Box sx={{
   display:'flex',
   justifyContent:'center',
   mb:1.5,
   opacity:.72
  }}>
   <RasanaLogo variant="mark" markSize={42}/>
  </Box>

  <Typography variant="subtitle1">{title}</Typography>

  <Typography
   variant="body2"
   color="text.secondary"
   mt={.5}
  >
   {description}
  </Typography>
 </Paper>;
}

export function LoadingState({label='Loading…'}:{label?:string}){
 return <Stack
  direction="row"
  spacing={1.5}
  alignItems="center"
  py={2}
 >
  <CircularProgress size={18} thickness={4}/>
  <Typography variant="body2" color="text.secondary">
   {label}
  </Typography>
 </Stack>;
}

export function BusyButton({
 busy=false,
 busyLabel='Processing…',
 children,
 disabled,
 startIcon,
 ...props
}:ButtonProps&{busy?:boolean;busyLabel?:string}){
 return <Button
  {...props}
  disabled={Boolean(disabled)||busy}
  startIcon={
   busy
    ?<CircularProgress size={16} color="inherit"/>
    :startIcon
  }
 >
  {busy?busyLabel:children}
 </Button>;
}

export function RouteLoadingState(){
 return <Box role="status" aria-live="polite" aria-label="Opening page"
  sx={{py:{xs:5,md:7},maxWidth:1100,mx:'auto'}}>
  <Stack spacing={2.5}>
   <Stack direction="row" spacing={1.5} alignItems="center">
    <CircularProgress size={24} thickness={4}/>
    <Box>
     <Typography variant="subtitle1">Opening your workspace…</Typography>
     <Typography variant="body2" color="text.secondary">
      Preparing this page for you.
     </Typography>
    </Box>
   </Stack>
   <LinearProgress sx={{height:3,borderRadius:2}} aria-label="Page loading"/>
   <Box sx={{display:'grid',gridTemplateColumns:{xs:'1fr',sm:'repeat(3,1fr)'},gap:2}}>
    {[0,1,2].map(i=><Paper key={i} variant="outlined"
     sx={{height:96,bgcolor:'background.paper',opacity:.6}}/>)}
   </Box>
  </Stack>
 </Box>;
}

export function GlobalActivity(){
 const fetching=useIsFetching();
 const mutating=useIsMutating();
 const active=fetching+mutating;
 const[visible,setVisible]=useState(false);
 useEffect(()=>{
  if(!active){setVisible(false);return;}
  const timer=window.setTimeout(()=>setVisible(true),180);
  return()=>window.clearTimeout(timer);
 },[Boolean(active)]);
 if(!active||!visible)return null;
 return <>
  <LinearProgress role="progressbar" aria-label={mutating?'Saving changes':'Loading data'} sx={{
   position:'fixed',top:0,left:0,right:0,
   zIndex:theme=>theme.zIndex.snackbar+2,height:3
  }}/>
  <Paper elevation={0} role="status" aria-live="polite" sx={{
   position:'fixed',top:12,right:16,
   zIndex:theme=>theme.zIndex.snackbar+1,px:1.5,py:.8,
   pointerEvents:'none',border:`1px solid ${rasanaTokens.borderSoft}`,
   boxShadow:rasanaTokens.shadowSoft
  }}>
   <Stack direction="row" spacing={1} alignItems="center">
    <CircularProgress size={14}/>
    <Typography variant="caption">
     {mutating?'Saving or processing changes…':'Loading information…'}
    </Typography>
   </Stack>
  </Paper>
 </>;
}
